// A section's Wash row on the Take Off (the template's row 64; #510): wash m²
// ÷ the wash rate, as labour. On an external area, Wash and Wash only come from
// site and can be changed for this option; the m² starts as the surfaces' total
// (shown greyed, the way a surface's rate shows its default) and is typed over
// for this option only. Internal areas have no wash: one typed at the desk
// before that still prices, with a notice to clear it.
import { setAreaWash, setAreaWashOnly, setWashArea, setWashRate, type AreaChoice, type PricedSection, type PricingRates, type SectionInfo } from '@higgins/m2q-pricing';
import { WASH_SIDE } from '../lib/site-wash';
import { NumberField } from './number-field';
import { formatTwo } from './surface-format';
import { CHANGED } from './take-off-rows';
import { Row, type Edit } from './take-off-extra-row';

interface Props {
  areaId: string;
  choice: AreaChoice | undefined;
  info: SectionInfo;
  /** Wash and Wash only as set on site (area-wash.tsx `siteWash`). */
  site: { wash: boolean; washOnly: boolean };
  priced: PricedSection;
  rates: PricingRates;
  onScope: Edit;
}

/** A tick for this option; yellow when it differs from the site's. */
function OptionTick({ label, checked, changed, onChange }: { label: string; checked: boolean; changed: boolean; onChange: (on: boolean) => void }) {
  return (
    <label className={`flex items-center gap-1 rounded px-1 ${changed ? CHANGED : ''}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-orange-500" />
      {label}
    </label>
  );
}

export function WashRow({ areaId, choice, info, site, priced, rates, onScope }: Props) {
  const wage = rates.labour.sell + rates.allowance.sell;
  const internal = info.side !== WASH_SIDE;
  if (internal && !info.internalWash) return null;
  const typed = choice?.washArea !== undefined;
  const area = (
    <>
      <NumberField
        compact
        value={choice?.washArea ?? null}
        placeholder={formatTwo(info.siteWashArea)}
        onChange={(v) => onScope((s) => setWashArea(s, areaId, v))}
        label="Wash area m² (clear for the surfaces' total)"
        className={`w-16 ${typed ? CHANGED : ''}`}
      />{' '}
      m² ÷
      <NumberField compact value={choice?.washRate ?? null} placeholder={formatTwo(rates.washRate)} onChange={(v) => onScope((s) => setWashRate(s, areaId, v))} label="Wash rate m²/hr" className="w-14" /> m²/hr
    </>
  );
  if (internal) {
    return (
      <Row hours={priced.washHours} sell={priced.washHours * wage}>
        Wash {area} <span className="text-xs text-amber-700">Wash is external only — clear this wash</span>
      </Row>
    );
  }
  return (
    <Row hours={priced.washHours} sell={priced.washHours * wage}>
      <OptionTick label="Wash" checked={info.wash} changed={info.wash !== site.wash} onChange={(on) => onScope((s) => setAreaWash(s, areaId, site, on))} />
      <OptionTick label="Wash only" checked={info.washOnly} changed={info.washOnly !== site.washOnly} onChange={(on) => onScope((s) => setAreaWashOnly(s, areaId, site, on))} />
      {info.wash && area}
      {info.wash && info.noGirthSurfaces.length > 0 && <span className="text-xs text-amber-700">No girth, not in the m²: {info.noGirthSurfaces.map((s) => s.name).join(', ')}</span>}
    </Row>
  );
}
